import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import VerifiedAddressSearch from "components/address/VerifiedAddressSearch";
import {
  addressRowKey,
  applyAddressCandidate,
  createAddressRow,
  isAddressRowFilled,
  isVerifiedAddressRow,
  normalizeAddressRow,
  stripAddressRowForSave,
  useStorefrontAddressLookup,
} from "lib/storefrontAddressLookup";
import { IosCard, iosColors } from "mobile/components/IosParity";

export default function MobileAddressManager({ addresses, companyKey, disabled, onSave }) {
  const [rows, setRows] = useState([]);
  const [notice, setNotice] = useState(null);
  const lookup = useStorefrontAddressLookup();
  const { syncRows } = lookup;

  useEffect(() => {
    setRows((Array.isArray(addresses) ? addresses : []).map((row) => normalizeAddressRow(row, companyKey)));
  }, [addresses, companyKey]);

  useEffect(() => {
    syncRows(rows, addressRowKey);
  }, [rows, syncRows]);

  const dirty = useMemo(() => JSON.stringify(rows.map(stripAddressRowForSave)) !== JSON.stringify((addresses || []).map(stripAddressRowForSave)), [rows, addresses]);

  const updateAddress = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === "formattedAddress" || field === "street" || field === "city" || field === "state" || field === "postalCode" || field === "country"
                ? { placeId: field === "formattedAddress" ? "" : row.placeId }
                : {}),
            }
          : row
      )
    );
  };

  const addAddress = () => {
    setRows((prev) => [...prev, createAddressRow(companyKey, { isPrimary: !prev.length })]);
  };

  const removeAddress = (index) => {
    lookup.clearRow(addressRowKey(rows[index], index));
    setRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  };

  const applySelectedCandidate = (index, candidate) => {
    const rowKey = addressRowKey(rows[index], index);
    lookup.applyCandidateForRow(rowKey, candidate);
    setRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? applyAddressCandidate(row, candidate) : row)));
  };

  const save = async () => {
    try {
      setNotice(null);
      const next = rows.map((row) => stripAddressRowForSave(row)).filter((row) => isAddressRowFilled(row));
      if (next.some((row) => !isVerifiedAddressRow(row))) {
        throw new Error("Choose a Google-verified suggestion for each saved address.");
      }
      await onSave(next);
      setNotice({ type: "success", text: "Addresses saved." });
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to save addresses." });
    }
  };

  return (
    <Stack spacing={0.85}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Stack spacing={0.1}>
          <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900 }}>
            Addresses
          </Typography>
          <Typography sx={{ color: iosColors.muted, fontSize: 12.25 }}>
            Search and save verified ship-to locations.
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.6}>
          <IconButton aria-label="Add address" disabled={disabled} onClick={addAddress} sx={{ border: `1px solid ${iosColors.line}`, bgcolor: "#fff" }}>
            <Icon>add_location_alt</Icon>
          </IconButton>
          <Button variant="contained" disabled={disabled || !dirty} onClick={save} startIcon={<Icon>save</Icon>}>
            Save
          </Button>
        </Stack>
      </Stack>

      {notice ? <Alert severity={notice.type} sx={{ borderRadius: 1 }}>{notice.text}</Alert> : null}

      {!rows.length ? (
        <IosCard>
          <Stack direction="row" spacing={0.85} alignItems="center">
            <Icon sx={{ color: iosColors.orange }}>location_off</Icon>
            <BoxText title="No addresses" text="Add a verified address before quoting shipping or creating fulfillment work." />
          </Stack>
        </IosCard>
      ) : null}

      {rows.map((address, index) => {
        const key = addressRowKey(address, index);
        const verified = isVerifiedAddressRow(address);
        return (
          <IosCard key={key}>
            <Stack spacing={0.8}>
              <Stack direction="row" justifyContent="space-between" spacing={0.75} alignItems="center">
                <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14.5, fontWeight: 900 }} noWrap>
                    {address.label || `Address ${index + 1}`}
                  </Typography>
                  <Stack direction="row" spacing={0.45} sx={{ flexWrap: "wrap", rowGap: 0.45 }}>
                    {address.isPrimary ? <Chip size="small" color="error" label="Primary" /> : null}
                    <Chip size="small" color={verified ? "success" : "default"} variant={verified ? "filled" : "outlined"} label={verified ? "Verified" : "Needs Google"} />
                  </Stack>
                </Stack>
                <IconButton aria-label="Remove address" disabled={disabled} onClick={() => removeAddress(index)}>
                  <Icon>delete</Icon>
                </IconButton>
              </Stack>

              <TextField label="Label" value={address.label || ""} disabled={disabled} onChange={(event) => updateAddress(index, "label", event.target.value)} />
              <TextField label="Recipient / business" value={address.name || ""} disabled={disabled} onChange={(event) => updateAddress(index, "name", event.target.value)} />
              <VerifiedAddressSearch
                rowKey={key}
                address={address}
                lookup={lookup}
                disabled={disabled}
                label="Find address or business"
                helperText="Choose the exact Google suggestion before saving."
                onTyping={() => updateAddress(index, "formattedAddress", "")}
                onSelect={(candidate) => applySelectedCandidate(index, candidate)}
              />
              <Stack direction="row" spacing={0.75}>
                <TextField label="Email" value={address.email || ""} disabled={disabled} onChange={(event) => updateAddress(index, "email", event.target.value)} />
                <TextField label="Phone" value={address.phone || ""} disabled={disabled} onChange={(event) => updateAddress(index, "phone", event.target.value)} />
              </Stack>
              {!address.isPrimary ? (
                <Button
                  variant="outlined"
                  disabled={disabled}
                  startIcon={<Icon>star</Icon>}
                  onClick={() => setRows((prev) => prev.map((row, rowIndex) => ({ ...row, isPrimary: rowIndex === index })))}
                >
                  Set primary
                </Button>
              ) : null}
            </Stack>
          </IosCard>
        );
      })}
    </Stack>
  );
}

function BoxText({ title, text }) {
  return (
    <Stack spacing={0.15}>
      <Typography sx={{ fontSize: 14.5, fontWeight: 900 }}>{title}</Typography>
      <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>{text}</Typography>
    </Stack>
  );
}

MobileAddressManager.propTypes = {
  addresses: PropTypes.arrayOf(PropTypes.shape({})),
  companyKey: PropTypes.string,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};
MobileAddressManager.defaultProps = {
  addresses: [],
  companyKey: "",
  disabled: false,
};
BoxText.propTypes = {
  title: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};
